import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  Chip,
  TextField,
  Button,
  Divider,
  Collapse,
  IconButton,
  Autocomplete,
  Slider,
  InputAdornment,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Clear as ClearIcon,
  FilterList as FilterListIcon,
  Search as SearchIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
// SearchFilters type removed with mock service
import { userService, organizationService, teamService, subscriptionService, paymentService, invitationService, roleService, contentService, tagService, searchService, authService } from '../../services';
import { useOrganization } from '../../context/OrganizationContext';
import { activeFilterCount } from '../../utils/activeFilterCount';

// Type alias for compatibility - Updated to match actual API response
interface RestTagGroup {
  id: string;
  name: string;
  description?: string;
  colorScheme?: string;  // API uses camelCase
  isOrdered?: boolean;
  allowMultiple?: boolean;  // API uses camelCase and it's optional
  organizationId?: string;
  teamId?: string;
  createdAt?: string;
  updatedAt?: string;
  tags?: Array<{
    id: string;
    name: string;
    orderInGroup?: number;
  }>;
  // Legacy fields for backward compatibility
  allow_multiple?: boolean;
  color_scheme?: string;
}
type TagGroup = RestTagGroup;

export interface ActiveFilters {
  mimeTypes: string[];
  tagGroupFilters: Record<string, string[]>; // tagGroupId -> selected tag IDs
  systemTags: string[];  // List of selected team-level tag IDs (inclusive filter)
  personalTags: string[];  // List of selected personal tag IDs (inclusive filter)
  tags: string[];  // General tags (backward compatibility)
  authors: string[];
  organizations: string[];
  teams: string[];
  dateRange: {
    start: Date | null;
    end: Date | null;
  };
  rating: {
    min: number;
    max: number;
  };
  bookmarkedOnly: boolean;  // Show only bookmarked content
  statuses: string[];  // Content status filter (draft, private, public, archive)
  // Deprecated - no longer used (tags are inclusive by default)
  personalTagsOnly: boolean;
  systemTagsOnly: boolean;
  // Legacy fields kept for backward compatibility but not used
  funnelStages: string[];
  personas: string[];
}

interface FilterSidebarProps {
  activeFilters: ActiveFilters;
  onChange: (filters: ActiveFilters) => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  showClearAll?: boolean;
  searchFacets?: any;  // Search facets from backend (includes author names, counts, etc.)
}

const defaultFilters: ActiveFilters = {
  mimeTypes: [],
  tagGroupFilters: {},
  systemTags: [],
  personalTags: [],
  tags: [],
  authors: [],
  organizations: [],
  teams: [],
  dateRange: {
    start: null,
    end: null,
  },
  rating: {
    min: 1,
    max: 5,
  },
  bookmarkedOnly: false,
  statuses: [],
  personalTagsOnly: false,
  systemTagsOnly: false,
  // Legacy fields
  funnelStages: [],
  personas: [],
};


// Grouped file type definitions for easier management
const fileTypeGroups = {
  views: {
    label: 'Views',
    mimeTypes: ['application/vnd.sharp.view']
  },
  pdf: {
    label: 'PDF Documents',
    mimeTypes: ['application/pdf']
  },
  videos: {
    label: 'Videos',
    mimeTypes: ['video/mp4', 'video/avi', 'video/mov', 'video/wmv', 'video/quicktime', 'video/x-msvideo', 'video/x-ms-wmv']
  },
  audio: {
    label: 'Audio',
    mimeTypes: ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/ogg', 'audio/aac', 'audio/flac', 'audio/x-m4a']
  },
  archives: {
    label: 'Archives',
    mimeTypes: [
      'application/zip',
      'application/x-zip-compressed',
      'application/x-tar',
      'application/gzip',
      'application/x-gzip',
    ],
  },
  images: {
    label: 'Images',
    mimeTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml', 'image/tiff']
  },
  word: {
    label: 'Word Documents',
    mimeTypes: ['application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']
  },
  powerpoint: {
    label: 'PowerPoint Presentations',
    mimeTypes: ['application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation']
  },
  excel: {
    label: 'Excel Spreadsheets',
    mimeTypes: ['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet']
  },
  text: {
    label: 'Text Files',
    mimeTypes: ['text/plain', 'text/csv', 'text/html', 'text/markdown']
  }
};

// Flattened mime type labels for backward compatibility
const mimeTypeLabels: Record<string, string> = {
  'application/vnd.sharp.view': 'Views',
  'application/pdf': 'PDF Documents',
  'video/mp4': 'MP4 Videos',
  'video/avi': 'AVI Videos',
  'video/mov': 'QuickTime Videos',
  'video/wmv': 'WMV Videos',
  'video/quicktime': 'QuickTime Videos',
  'video/x-msvideo': 'AVI Videos',
  'video/x-ms-wmv': 'WMV Videos',
  'audio/mpeg': 'MP3 Audio',
  'audio/mp3': 'MP3 Audio',
  'audio/wav': 'WAV Audio',
  'audio/ogg': 'OGG Audio',
  'audio/aac': 'AAC Audio',
  'audio/flac': 'FLAC Audio',
  'audio/x-m4a': 'M4A Audio',
  'application/zip': 'ZIP Archives',
  'application/x-zip-compressed': 'ZIP Archives',
  'application/x-tar': 'TAR Archives',
  'application/gzip': 'GZIP Archives',
  'application/x-gzip': 'GZIP Archives',
  'image/jpeg': 'JPEG Images',
  'image/png': 'PNG Images',
  'image/gif': 'GIF Images',
  'image/webp': 'WebP Images',
  'image/svg+xml': 'SVG Graphics',
  'image/tiff': 'TIFF Images',
  'application/vnd.ms-powerpoint': 'PowerPoint (Legacy)',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PowerPoint (Modern)',
  'application/msword': 'Word (Legacy)',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word (Modern)',
  'application/vnd.ms-excel': 'Excel (Legacy)',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel (Modern)',
  'text/plain': 'Plain Text',
  'text/csv': 'CSV Files',
  'text/html': 'HTML Files',
  'text/markdown': 'Markdown Files',
};

// Status options
const statusOptions = [
  { value: 'draft', label: 'Draft' },
  { value: 'private', label: 'Private' },
  { value: 'public', label: 'Public' },
  { value: 'archive', label: 'Archived' },
];

const FilterSidebar: React.FC<FilterSidebarProps> = ({
  activeFilters = defaultFilters,
  onChange,
  collapsed = false,
  onToggleCollapse,
  showClearAll = true,
  searchFacets,
}) => {
  // Ensure backward compatibility with old saved state that doesn't have statuses field
  const safeActiveFilters: ActiveFilters = {
    ...defaultFilters,
    ...activeFilters,
    statuses: activeFilters.statuses || [],
  };

  const [availableFilters, setAvailableFilters] = useState<any | null>(null);
  const [tagGroups, setTagGroups] = useState<TagGroup[]>([]);
  const [systemTags, setSystemTags] = useState<Array<{id: string; name: string; scope?: string}>>([]);
  const [personalTags, setPersonalTags] = useState<Array<{id: string; name: string; scope?: string}>>([]);
  const [expandedPanels, setExpandedPanels] = useState<string[]>([
    'contentTypes',
    'mimeTypes',
  ]);
  const [tagSearchValue, setTagSearchValue] = useState('');
  const [loadingError, setLoadingError] = useState<string | null>(null);
  const [hasInitializedExpanded, setHasInitializedExpanded] = useState(false);

  // Get organization context
  const { currentOrganizationId, currentTeamId } = useOrganization();

  // Reset initialization flag when organization or team changes
  useEffect(() => {
    setHasInitializedExpanded(false);
  }, [currentOrganizationId, currentTeamId]);

  // Load available filters and tag groups
  useEffect(() => {
    const loadFilters = async () => {
      if (!currentOrganizationId) {
        console.warn('No organization selected, skipping filter load');
        setAvailableFilters(null);
        setTagGroups([]);
        setSystemTags([]);
        setPersonalTags([]);
        return;
      }

      try {
        setLoadingError(null);
        
        const [filters, groupsWithTagsResponse, allTagsResponse] = await Promise.all([
          // Mock available filters for now - authors will come from search facets
          Promise.resolve({
            mimeTypes: [
              'application/vnd.sharp.view',
              'application/pdf',
              'video/mp4', 'video/avi', 'video/mov', 'video/wmv', 'video/quicktime', 'video/x-msvideo', 'video/x-ms-wmv',
              'audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/ogg', 'audio/aac', 'audio/flac', 'audio/x-m4a',
              'application/zip', 'application/x-zip-compressed', 'application/x-tar', 'application/gzip', 'application/x-gzip',
              'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml', 'image/tiff',
              'application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
              'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
              'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
              'text/plain', 'text/csv', 'text/html', 'text/markdown'
            ],
            tags: [], // Will be populated from tag groups
            authors: searchFacets?.authors?.map((facet: any) => ({
              id: facet.value,
              name: facet.label,
              count: facet.count
            })) || [],
            organizations: [],
            teams: []
          }),
          tagService.getTagGroupsWithTags({
            organization_id: currentOrganizationId,
            team_id: currentTeamId
          }).catch(error => {
            console.error('Error loading tag groups with tags:', error);
            return [];
          }),
          // Get all tags (both team and personal) for this organization and team
          tagService.listUnifiedTags({
            tag_type: 'all',
            organization_id: currentOrganizationId,
            team_id: currentTeamId,
            limit: 1000,  // High limit to get all tags for filtering
            sort_by: 'name',
            sort_order: 'asc'
          }).catch(error => {
            console.error('Error loading all tags:', error);
            return [];
          })
        ]);

        // Handle the nested response structure for tag groups with tags
        let groups: TagGroup[] = [];

        // Process tag groups response
        if (Array.isArray(groupsWithTagsResponse)) {
          groups = groupsWithTagsResponse;
        }

        // Process all tags and properly separate them
        let systemTagsList: Array<{id: string; name: string; scope?: string; teamId?: string}> = [];
        let personalTagsList: Array<{id: string; name: string; scope?: string; teamId?: string}> = [];

        if (Array.isArray(allTagsResponse)) {
          // Filter tags to ensure they match the current team
          allTagsResponse.forEach((tag: any) => {
            // Only include tags that belong to the current team
            if (tag.teamId === currentTeamId || tag.team_id === currentTeamId) {
              const tagObj = {
                id: tag.id,
                name: tag.name,
                scope: tag.scope,
                teamId: tag.teamId || tag.team_id
              };

              if (tag.scope === 'team') {
                systemTagsList.push(tagObj);
              } else if (tag.scope === 'personal') {
                personalTagsList.push(tagObj);
              }
            }
          });
        }
        
        setAvailableFilters(filters);
        setTagGroups(groups || []);
        setSystemTags(systemTagsList);
        setPersonalTags(personalTagsList);

        // Auto-expand the first few filter sections by default (only on first load)
        if (!hasInitializedExpanded) {
          const defaultExpanded = ['quickFilters'];
          if (Array.isArray(groups) && groups.length > 0) {
            defaultExpanded.push(groups[0].id);
          }
          if (systemTagsList.length > 0) {
            defaultExpanded.push('systemTags');
          }
          if (personalTagsList.length > 0) {
            defaultExpanded.push('personalTags');
          }
          setExpandedPanels(defaultExpanded);
          setHasInitializedExpanded(true);
        }
      } catch (error) {
        console.error('Error loading filters:', error);
        setLoadingError(error instanceof Error ? error.message : 'Failed to load filters');
        // Set fallback data on error
        setAvailableFilters({
          mimeTypes: ['application/pdf', 'video/mp4', 'image/jpeg', 'image/png'],
          tags: [],
          authors: [],
          organizations: [],
          teams: []
        });
        setTagGroups([]);
        setSystemTags([]);
        setPersonalTags([]);
      }
    };
    loadFilters();
  }, [currentOrganizationId, currentTeamId, searchFacets]);

  // Listen for tag cache invalidation events to refresh tags
  useEffect(() => {
    const handleTagsUpdated = () => {
      console.log('Tags updated event received, refreshing filter sidebar tags');
      // Force reload of tags when they are updated elsewhere
      if (currentOrganizationId) {
        // Small delay to ensure backend has processed changes
        setTimeout(async () => {
          try {
            const [groupsWithTagsResponse, allTagsResponse] = await Promise.all([
              tagService.getTagGroupsWithTags({
                organization_id: currentOrganizationId,
                team_id: currentTeamId
              }).catch(error => {
                console.error('Error reloading tag groups:', error);
                return [];
              }),
              tagService.listUnifiedTags({
                tag_type: 'all',
                organization_id: currentOrganizationId,
                team_id: currentTeamId,
                limit: 1000,  // High limit to get all tags for filtering
                sort_by: 'name',
                sort_order: 'asc'
              }).catch(error => {
                console.error('Error reloading all tags:', error);
                return [];
              })
            ]);

            // Process reloaded tags
            let groups: TagGroup[] = [];
            if (Array.isArray(groupsWithTagsResponse)) {
              groups = groupsWithTagsResponse;
            }

            // Process all tags and properly separate them
            let systemTagsList: Array<{id: string; name: string; scope?: string; teamId?: string}> = [];
            let personalTagsList: Array<{id: string; name: string; scope?: string; teamId?: string}> = [];

            if (Array.isArray(allTagsResponse)) {
              // Filter tags to ensure they match the current team
              allTagsResponse.forEach((tag: any) => {
                // Only include tags that belong to the current team
                if (tag.teamId === currentTeamId || tag.team_id === currentTeamId) {
                  const tagObj = {
                    id: tag.id,
                    name: tag.name,
                    scope: tag.scope,
                    teamId: tag.teamId || tag.team_id
                  };

                  if (tag.scope === 'team') {
                    systemTagsList.push(tagObj);
                  } else if (tag.scope === 'personal') {
                    personalTagsList.push(tagObj);
                  }
                }
              });
            }
            
            setTagGroups(groups || []);
            setSystemTags(systemTagsList);
            setPersonalTags(personalTagsList);
            console.log('Tags refreshed successfully');
          } catch (error) {
            console.error('Error refreshing tags:', error);
          }
        }, 500);
      }
    };

    // Listen for custom event that can be dispatched when tags are updated
    window.addEventListener('tags-updated', handleTagsUpdated);
    
    return () => {
      window.removeEventListener('tags-updated', handleTagsUpdated);
    };
  }, [currentOrganizationId, currentTeamId]);

  // Handle panel expansion
  const handlePanelChange = (panel: string) => (
    event: React.SyntheticEvent,
    isExpanded: boolean,
  ) => {
    setExpandedPanels(prev =>
      isExpanded
        ? [...prev, panel]
        : prev.filter(p => p !== panel)
    );
  };

  // Handle filter changes
  const handleFilterChange = (filterType: keyof ActiveFilters, value: any) => {
    onChange({
      ...safeActiveFilters,
      [filterType]: value,
    });
  };

  // Handle boolean filter toggle
  const handleBooleanFilterToggle = (filterType: 'bookmarkedOnly') => {
    onChange({
      ...safeActiveFilters,
      [filterType]: !safeActiveFilters[filterType],
    });
  };

  // Handle multi-select changes
  const handleMultiSelectChange = (
    filterType: keyof ActiveFilters,
    value: string,
    checked: boolean,
  ) => {
    const currentValues = safeActiveFilters[filterType] as string[];
    const newValues = checked
      ? [...currentValues, value]
      : currentValues.filter(v => v !== value);

    handleFilterChange(filterType, newValues);
  };

  // Handle tag group filter changes
  const handleTagGroupFilterChange = (
    tagGroupId: string,
    tagId: string,
    checked: boolean,
  ) => {
    const currentFilters = { ...safeActiveFilters.tagGroupFilters };
    const currentValues = currentFilters[tagGroupId] || [];

    if (checked) {
      currentFilters[tagGroupId] = [...currentValues, tagId];
    } else {
      currentFilters[tagGroupId] = currentValues.filter(id => id !== tagId);
      // Remove empty arrays
      if (currentFilters[tagGroupId].length === 0) {
        delete currentFilters[tagGroupId];
      }
    }

    handleFilterChange('tagGroupFilters', currentFilters);
  };

  // Handle tag group radio change (for exclusive groups)
  const handleTagGroupRadioChange = (
    tagGroupId: string,
    tagId: string | null,
  ) => {
    const currentFilters = { ...safeActiveFilters.tagGroupFilters };

    if (tagId) {
      currentFilters[tagGroupId] = [tagId];
    } else {
      delete currentFilters[tagGroupId];
    }

    handleFilterChange('tagGroupFilters', currentFilters);
  };

  // Clear all filters
  const handleClearAll = () => {
    onChange(defaultFilters);
    setTagSearchValue('');
  };

  // Clear specific filter type
  const handleClearFilterType = (filterType: keyof ActiveFilters) => {
    if (filterType === 'dateRange') {
      handleFilterChange(filterType, { start: null, end: null });
    } else if (filterType === 'rating') {
      handleFilterChange(filterType, { min: 1, max: 5 });
    } else if (filterType === 'tagGroupFilters') {
      handleFilterChange(filterType, {});
    } else if (typeof safeActiveFilters[filterType] === 'boolean') {
      handleFilterChange(filterType, false);
    } else {
      handleFilterChange(filterType, []);
    }
  };

  // Clear specific tag group
  const handleClearTagGroup = (tagGroupId: string) => {
    const currentFilters = { ...safeActiveFilters.tagGroupFilters };
    delete currentFilters[tagGroupId];
    handleFilterChange('tagGroupFilters', currentFilters);
  };

  // Get active tag count for a specific tag group
  const getTagGroupActiveCount = (tagGroupId: string) => {
    return safeActiveFilters.tagGroupFilters[tagGroupId]?.length || 0;
  };

  // Check if a tag is selected in a group
  const isTagSelected = (tagGroupId: string, tagId: string) => {
    return safeActiveFilters.tagGroupFilters[tagGroupId]?.includes(tagId) || false;
  };

  // Get selected tag ID for exclusive groups
  const getSelectedTagId = (tagGroupId: string) => {
    const selected = safeActiveFilters.tagGroupFilters[tagGroupId];
    return selected && selected.length > 0 ? selected[0] : '';
  };

  // Handle grouped file type selection (e.g., all Word formats)
  const handleFileTypeGroupChange = (groupKey: string, checked: boolean) => {
    const group = fileTypeGroups[groupKey as keyof typeof fileTypeGroups];
    const currentMimeTypes = safeActiveFilters.mimeTypes;

    let newMimeTypes: string[];
    if (checked) {
      // Add all mime types from this group
      newMimeTypes = [...new Set([...currentMimeTypes, ...group.mimeTypes])];
    } else {
      // Remove all mime types from this group
      newMimeTypes = currentMimeTypes.filter(mt => !group.mimeTypes.includes(mt));
    }

    handleFilterChange('mimeTypes', newMimeTypes);
  };

  // Check if all mime types in a group are selected
  const isFileTypeGroupSelected = (groupKey: string) => {
    const group = fileTypeGroups[groupKey as keyof typeof fileTypeGroups];
    return group.mimeTypes.every(mt => safeActiveFilters.mimeTypes.includes(mt));
  };

  // Check if some (but not all) mime types in a group are selected
  const isFileTypeGroupIndeterminate = (groupKey: string) => {
    const group = fileTypeGroups[groupKey as keyof typeof fileTypeGroups];
    const selectedCount = group.mimeTypes.filter(mt => safeActiveFilters.mimeTypes.includes(mt)).length;
    return selectedCount > 0 && selectedCount < group.mimeTypes.length;
  };

  const filterCount = activeFilterCount(safeActiveFilters);

  if (loadingError) {
    return (
      <Paper elevation={0} sx={{ p: 2, height: 'fit-content', border: '1px solid', borderColor: 'divider', borderRadius: '14px' }}>
        <Typography color="error" variant="body2">
          Error loading filters: {loadingError}
        </Typography>
        <Button size="small" onClick={() => window.location.reload()} sx={{ mt: 1 }}>
          Reload Page
        </Button>
      </Paper>
    );
  }

  if (!currentOrganizationId) {
    return (
      <Paper elevation={0} sx={{ p: 2, height: 'fit-content', border: '1px solid', borderColor: 'divider', borderRadius: '14px' }}>
        <Typography variant="body2" color="text.secondary">
          Please select an organization to view filters
        </Typography>
      </Paper>
    );
  }

  if (!availableFilters) {
    return (
      <Paper elevation={0} sx={{ p: 2, height: 'fit-content', border: '1px solid', borderColor: 'divider', borderRadius: '14px' }}>
        <Typography variant="body2" color="text.secondary">Loading filters...</Typography>
      </Paper>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Paper
        elevation={0}
        sx={{
          height: 'fit-content',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '14px',
          bgcolor: 'background.paper',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'grey.50',
            borderRadius: '14px 14px 0 0',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterListIcon color="primary" fontSize="small" />
            <Typography
              sx={{
                fontSize: '10.5px',
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: 'text.secondary',
              }}
            >
              Filters
            </Typography>
            {filterCount > 0 && (
              <Chip
                size="small"
                label={filterCount}
                color="primary"
                variant="filled"
                sx={{ fontWeight: 600, height: 18, fontSize: '0.65rem' }}
              />
            )}
          </Box>
          {onToggleCollapse && (
            <IconButton size="small" onClick={onToggleCollapse}>
              <ExpandMoreIcon
                sx={{
                  transform: collapsed ? 'rotate(0deg)' : 'rotate(180deg)',
                  transition: 'transform 0.2s',
                }}
              />
            </IconButton>
          )}
        </Box>

        <Collapse in={!collapsed}>
          <Box sx={{ p: 2, pt: 1.5 }}>
            {/* Clear All Button */}
            {showClearAll && filterCount > 0 && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<ClearIcon />}
                onClick={handleClearAll}
                fullWidth
                sx={{
                  mb: 2,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.75rem',
                  '&:hover': {
                    bgcolor: 'error.50',
                    borderColor: 'error.main',
                    color: 'error.main',
                  }
                }}
              >
                Clear All Filters
              </Button>
            )}


            {/* File Type Filter - Grouped by category */}
            <Accordion
              expanded={expandedPanels.includes('mimeTypes')}
              onChange={handlePanelChange('mimeTypes')}
              elevation={0}
              sx={{
                '&:before': { display: 'none' },
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '8px',
                mb: 1.5,
                '&.Mui-expanded': {
                  margin: '0 0 12px 0',
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}
                sx={{
                  minHeight: 'auto',
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'grey.50',
                  borderRadius: '8px 8px 0 0',
                  '&.Mui-expanded': { minHeight: 'auto' },
                  '& .MuiAccordionSummary-content': { my: '8px' },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <Typography
                    sx={{ fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.secondary' }}
                  >
                    File Type
                  </Typography>
                  {safeActiveFilters.mimeTypes.length > 0 && (
                    <Chip size="small" label={safeActiveFilters.mimeTypes.length} color="secondary"
                      sx={{ fontWeight: 600, height: 18, fontSize: '0.65rem' }} />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0.5, pb: 1 }}>
                <FormControl component="fieldset" fullWidth>
                  <FormGroup>
                    {Object.entries(fileTypeGroups).map(([groupKey, group]) => (
                      <FormControlLabel
                        key={groupKey}
                        control={
                          <Checkbox
                            size="small"
                            checked={isFileTypeGroupSelected(groupKey)}
                            indeterminate={isFileTypeGroupIndeterminate(groupKey)}
                            onChange={(e) =>
                              handleFileTypeGroupChange(groupKey, e.target.checked)
                            }
                            sx={{
                              py: 0.5,
                              '&.Mui-checked': { color: 'secondary.main' },
                            }}
                          />
                        }
                        label={group.label}
                        sx={{
                          ml: 0,
                          mr: 0,
                          '&:hover': {
                            bgcolor: 'action.hover',
                            borderRadius: 1,
                          },
                          '& .MuiFormControlLabel-label': {
                            fontSize: '0.8125rem',
                            fontWeight: 500,
                          },
                        }}
                      />
                    ))}
                  </FormGroup>
                </FormControl>
              </AccordionDetails>
            </Accordion>

            {/* Quick Filter Toggles */}
            <Accordion
              expanded={expandedPanels.includes('quickFilters')}
              onChange={handlePanelChange('quickFilters')}
              elevation={0}
              sx={{
                '&:before': { display: 'none' },
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '8px',
                mb: 1.5,
                '&.Mui-expanded': {
                  margin: '0 0 12px 0',
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}
                sx={{
                  minHeight: 'auto',
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'grey.50',
                  borderRadius: '8px 8px 0 0',
                  '&.Mui-expanded': { minHeight: 'auto' },
                  '& .MuiAccordionSummary-content': { my: '8px' },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <Typography
                    sx={{ fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.secondary' }}
                  >
                    Quick Filters
                  </Typography>
                  {safeActiveFilters.bookmarkedOnly && (
                    <Chip size="small" label="1" color="info"
                      sx={{ fontWeight: 600, height: 18, fontSize: '0.65rem' }} />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0.5, pb: 1 }}>
                <FormControl component="fieldset">
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={safeActiveFilters.bookmarkedOnly}
                          onChange={() => handleBooleanFilterToggle('bookmarkedOnly')}
                          sx={{
                            py: 0.5,
                            '&.Mui-checked': { color: 'info.main' },
                          }}
                        />
                      }
                      label="Bookmarked"
                      sx={{
                        ml: 0,
                        mr: 0,
                        '&:hover': {
                          bgcolor: 'action.hover',
                          borderRadius: 1,
                        },
                        '& .MuiFormControlLabel-label': {
                          fontSize: '0.8125rem',
                        },
                      }}
                    />
                  </FormGroup>
                </FormControl>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, ml: 0.5 }}>
                  Show only content that you have bookmarked
                </Typography>
              </AccordionDetails>
            </Accordion>

            {/* System Tags Filter - Including tag groups and solo tags */}
            {(systemTags.length > 0 || tagGroups.some(g => g.tags && g.tags.length > 0)) && (
              <Accordion
                expanded={expandedPanels.includes('systemTags')}
                onChange={handlePanelChange('systemTags')}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '8px',
                  '&:before': { display: 'none' },
                  boxShadow: 'none',
                  mb: 1.5,
                  '&.Mui-expanded': { margin: '0 0 12px 0' },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    minHeight: 'auto',
                    bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(34,181,220,0.06)' : 'rgba(27,143,184,0.06)',
                    borderRadius: '8px 8px 0 0',
                    '&.Mui-expanded': { minHeight: 'auto' },
                    '& .MuiAccordionSummary-content': { my: '8px' },
                    '&:hover': {
                      bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(34,181,220,0.1)' : 'rgba(27,143,184,0.1)',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <Typography
                      sx={{ fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.secondary' }}
                    >
                      Team Tags
                    </Typography>
                    {safeActiveFilters.systemTags && safeActiveFilters.systemTags.length > 0 && (
                      <Chip size="small" label={safeActiveFilters.systemTags.length} color="info"
                        sx={{ height: 18, fontSize: '0.65rem' }} />
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0.5, pb: 1 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {/* Tag Groups */}
                    {tagGroups.filter(group => group.tags && group.tags.length > 0).map((tagGroup) => {
                      const groupTags = (tagGroup.tags || []) as any[];
                      const selectedCount = groupTags.filter(tag =>
                        safeActiveFilters.systemTags?.includes(tag.id)
                      ).length;
                      const isGroupExpanded = expandedPanels.includes(`tagGroup_${tagGroup.id}`);
                      const allSelected = groupTags.length > 0 && selectedCount === groupTags.length;
                      const someSelected = selectedCount > 0 && selectedCount < groupTags.length;

                      return (
                        <Box key={tagGroup.id} sx={{ borderLeft: '2px solid', borderColor: (theme) => theme.palette.mode === 'dark' ? 'brand.cyan' : 'brand.teal', pl: 1 }}>
                          {/* Tag Group Header */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Checkbox
                              size="small"
                              checked={allSelected}
                              indeterminate={someSelected}
                              onChange={(e) => {
                                const currentTags = safeActiveFilters.systemTags || [];
                                const groupTagIds = groupTags.map(t => t.id);
                                let newTags: string[];

                                if (e.target.checked) {
                                  // Add all group tags
                                  newTags = [...new Set([...currentTags, ...groupTagIds])];
                                } else {
                                  // Remove all group tags
                                  newTags = currentTags.filter(id => !groupTagIds.includes(id));
                                }

                                onChange({ ...activeFilters, systemTags: newTags });
                              }}
                              sx={{ py: 0.5 }}
                            />
                            <Box 
                              sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'space-between',
                                flex: 1,
                                cursor: 'pointer',
                                '&:hover': { bgcolor: 'action.hover' },
                                borderRadius: 1,
                                p: 0.5
                              }}
                              onClick={() => {
                                const newPanels = expandedPanels.includes(`tagGroup_${tagGroup.id}`)
                                  ? expandedPanels.filter(p => p !== `tagGroup_${tagGroup.id}`)
                                  : [...expandedPanels, `tagGroup_${tagGroup.id}`];
                                setExpandedPanels(newPanels);
                              }}
                            >
                              <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'text.primary' }}>
                                {tagGroup.name}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {selectedCount > 0 && (
                                  <Chip size="small" label={`${selectedCount}/${groupTags.length}`}
                                    sx={{ height: 16, fontSize: '0.6rem', '& .MuiChip-label': { px: 0.75 } }} />
                                )}
                                {isGroupExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                              </Box>
                            </Box>
                          </Box>
                          
                          {/* Tag Group Items */}
                          <Collapse in={isGroupExpanded}>
                            <Box sx={{ pl: 4, pt: 1 }}>
                              <FormGroup>
                                {groupTags.map((tag: any) => (
                                  <FormControlLabel
                                    key={tag.id}
                                    control={
                                      <Checkbox
                                        size="small"
                                        checked={safeActiveFilters.systemTags?.includes(tag.id) || false}
                                        onChange={(e) => {
                                          const currentTags = safeActiveFilters.systemTags || [];
                                          const newTags = e.target.checked
                                            ? [...currentTags, tag.id]
                                            : currentTags.filter(id => id !== tag.id);
                                          onChange({ ...activeFilters, systemTags: newTags });
                                        }}
                                      />
                                    }
                                    label={
                                      <Typography sx={{ fontSize: '0.8125rem' }}>
                                        {tag.name}
                                      </Typography>
                                    }
                                  />
                                ))}
                              </FormGroup>
                            </Box>
                          </Collapse>
                        </Box>
                      );
                    })}
                    
                    {/* Solo System Tags (not in any group) */}
                    {(() => {
                      const tagsInGroups = new Set(
                        tagGroups.flatMap(g => (g.tags || []).map((t: any) => t.id))
                      );
                      // Filter solo tags using same logic as TagManager: team scope + not in group
                      const soloTags = systemTags.filter(tag => {
                        const isNotInGroup = !tagsInGroups.has(tag.id);
                        const hasTeamScope = tag.scope === 'team';
                        return isNotInGroup && hasTeamScope;
                      });
                      
                      if (soloTags.length === 0) return null;
                      
                      return (
                        <Box sx={{ pt: tagGroups.some(g => g.tags && g.tags.length > 0) ? 1 : 0 }}>
                          {tagGroups.some(g => g.tags && g.tags.length > 0) && (
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
                              Other Tags
                            </Typography>
                          )}
                          <FormGroup>
                            {soloTags.map((tag) => (
                              <FormControlLabel
                                key={tag.id}
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={safeActiveFilters.systemTags?.includes(tag.id) || false}
                                    onChange={(e) => {
                                      const currentTags = safeActiveFilters.systemTags || [];
                                      const newTags = e.target.checked
                                        ? [...currentTags, tag.id]
                                        : currentTags.filter(id => id !== tag.id);
                                      onChange({ ...activeFilters, systemTags: newTags });
                                    }}
                                    sx={{ py: 0.5 }}
                                  />
                                }
                                label={tag.name}
                                sx={{
                                  ml: 0,
                                  '& .MuiFormControlLabel-label': { fontSize: '0.8125rem' },
                                }}
                              />
                            ))}
                          </FormGroup>
                        </Box>
                      );
                    })()}
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}

            {/* Personal Tags Filter */}
            {personalTags.length > 0 && (
              <Accordion
                expanded={expandedPanels.includes('personalTags')}
                onChange={handlePanelChange('personalTags')}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '8px',
                  '&:before': { display: 'none' },
                  boxShadow: 'none',
                  mb: 1.5,
                  '&.Mui-expanded': { margin: '0 0 12px 0' },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    minHeight: 'auto',
                    bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,183,77,0.06)' : 'rgba(237,108,2,0.05)',
                    borderRadius: '8px 8px 0 0',
                    '&.Mui-expanded': { minHeight: 'auto' },
                    '& .MuiAccordionSummary-content': { my: '8px' },
                    '&:hover': {
                      bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,183,77,0.1)' : 'rgba(237,108,2,0.08)',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <Typography
                      sx={{ fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.secondary' }}
                    >
                      My Tags
                    </Typography>
                    {safeActiveFilters.personalTags && safeActiveFilters.personalTags.length > 0 && (
                      <Chip size="small" label={safeActiveFilters.personalTags.length} color="warning"
                        sx={{ height: 18, fontSize: '0.65rem' }} />
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0.5, pb: 1 }}>
                  <FormControl component="fieldset">
                    <FormGroup>
                      {personalTags.map((tag) => (
                        <FormControlLabel
                          key={tag.id}
                          control={
                            <Checkbox
                              size="small"
                              checked={safeActiveFilters.personalTags?.includes(tag.id) || false}
                              onChange={(e) => {
                                const currentTags = safeActiveFilters.personalTags || [];
                                const newTags = e.target.checked
                                  ? [...currentTags, tag.id]
                                  : currentTags.filter(id => id !== tag.id);
                                onChange({ ...activeFilters, personalTags: newTags });
                              }}
                              sx={{ py: 0.5 }}
                            />
                          }
                          label={tag.name}
                          sx={{
                            ml: 0,
                            '& .MuiFormControlLabel-label': { fontSize: '0.8125rem' },
                          }}
                        />
                      ))}
                    </FormGroup>
                  </FormControl>
                </AccordionDetails>
              </Accordion>
            )}

            {/* Authors Filter */}
            <Accordion
              expanded={expandedPanels.includes('authors')}
              onChange={handlePanelChange('authors')}
              elevation={0}
              sx={{
                '&:before': { display: 'none' },
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '8px',
                mb: 1.5,
                '&.Mui-expanded': { margin: '0 0 12px 0' },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}
                sx={{
                  minHeight: 'auto',
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'grey.50',
                  borderRadius: '8px 8px 0 0',
                  '&.Mui-expanded': { minHeight: 'auto' },
                  '& .MuiAccordionSummary-content': { my: '8px' },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <Typography
                    sx={{ fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.secondary' }}
                  >
                    Authors
                  </Typography>
                  {safeActiveFilters.authors.length > 0 && (
                    <Chip size="small" label={safeActiveFilters.authors.length} color="warning"
                      sx={{ height: 18, fontSize: '0.65rem' }} />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0.5, pb: 1 }}>
                <Box sx={{ mb: 1 }}>
                  <Autocomplete
                    multiple
                    options={availableFilters.authors}
                    value={availableFilters.authors.filter((author: any) =>
                      safeActiveFilters.authors.includes(author.id)
                    )}
                    onChange={(event, newValue) =>
                      handleFilterChange('authors', newValue.map((author: any) => author.id))
                    }
                    getOptionLabel={(option: any) =>
                      typeof option === 'string' ? option : `${option.name} (${option.count})`
                    }
                    isOptionEqualToValue={(option: any, value: any) =>
                      (typeof option === 'string' ? option : option.id) === (typeof value === 'string' ? value : value.id)
                    }
                    renderTags={(value, getTagProps) =>
                      value.map((option: any, index: number) => (
                        <Chip
                          variant="outlined"
                          label={typeof option === 'string' ? option : option.name}
                          color="warning"
                          {...getTagProps({ index })}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder="Search authors..."
                        size="small"
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Tags Filter - Removed in favor of System Tags and Personal Tags sections */}
            {/* <Accordion
              expanded={expandedPanels.includes('tags')}
              onChange={handlePanelChange('tags')}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <Typography>Tags</Typography>
                  {safeActiveFilters.tags.length > 0 && (
                    <Chip size="small" label={safeActiveFilters.tags.length} color="default" />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ mb: 2 }}>
                  <Autocomplete
                    multiple
                    options={availableFilters.tags}
                    value={safeActiveFilters.tags}
                    onChange={(event, newValue) => handleFilterChange('tags', newValue)}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip variant="outlined" label={option} {...getTagProps({ index })} />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder="Search tags..."
                        size="small"
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                </Box>
              </AccordionDetails>
            </Accordion> */}

            {/* Date Range Filter */}
            <Accordion
              expanded={expandedPanels.includes('dateRange')}
              onChange={handlePanelChange('dateRange')}
              elevation={0}
              sx={{
                '&:before': { display: 'none' },
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '8px',
                mb: 1.5,
                '&.Mui-expanded': { margin: '0 0 12px 0' },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}
                sx={{
                  minHeight: 'auto',
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'grey.50',
                  borderRadius: '8px 8px 0 0',
                  '&.Mui-expanded': { minHeight: 'auto' },
                  '& .MuiAccordionSummary-content': { my: '8px' },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <Typography
                    sx={{ fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.secondary' }}
                  >
                    Date Range
                  </Typography>
                  {(safeActiveFilters.dateRange.start || safeActiveFilters.dateRange.end) && (
                    <Chip size="small" label="1" color="warning"
                      sx={{ height: 18, fontSize: '0.65rem' }} />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0.5, pb: 1 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <DatePicker
                    label="Start Date"
                    value={safeActiveFilters.dateRange.start}
                    onChange={(newValue) =>
                      handleFilterChange('dateRange', {
                        ...safeActiveFilters.dateRange,
                        start: newValue,
                      })
                    }
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: true,
                      },
                    }}
                  />
                  <DatePicker
                    label="End Date"
                    value={safeActiveFilters.dateRange.end}
                    onChange={(newValue) =>
                      handleFilterChange('dateRange', {
                        ...safeActiveFilters.dateRange,
                        end: newValue,
                      })
                    }
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: true,
                      },
                    }}
                  />
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Rating Filter */}
            <Accordion
              expanded={expandedPanels.includes('rating')}
              onChange={handlePanelChange('rating')}
              elevation={0}
              sx={{
                '&:before': { display: 'none' },
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '8px',
                mb: 1.5,
                '&.Mui-expanded': { margin: '0 0 12px 0' },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}
                sx={{
                  minHeight: 'auto',
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'grey.50',
                  borderRadius: '8px 8px 0 0',
                  '&.Mui-expanded': { minHeight: 'auto' },
                  '& .MuiAccordionSummary-content': { my: '8px' },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <Typography
                    sx={{ fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.secondary' }}
                  >
                    Rating
                  </Typography>
                  {(safeActiveFilters.rating.min !== 1 || safeActiveFilters.rating.max !== 5) && (
                    <Chip size="small" label="1" color="success"
                      sx={{ height: 18, fontSize: '0.65rem' }} />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0.5, pb: 1 }}>
                <Box sx={{ px: 1 }}>
                  <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block' }}>
                    Rating: {safeActiveFilters.rating.min} - {safeActiveFilters.rating.max} stars
                  </Typography>
                  <Slider
                    value={[safeActiveFilters.rating.min, safeActiveFilters.rating.max]}
                    onChange={(event, newValue) =>
                      handleFilterChange('rating', {
                        min: (newValue as number[])[0],
                        max: (newValue as number[])[1],
                      })
                    }
                    valueLabelDisplay="auto"
                    min={1}
                    max={5}
                    step={0.5}
                    marks={[
                      { value: 1, label: '1⭐' },
                      { value: 3, label: '3⭐' },
                      { value: 5, label: '5⭐' },
                    ]}
                  />
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Status Filter */}
            <Accordion
              expanded={expandedPanels.includes('statuses')}
              onChange={handlePanelChange('statuses')}
              elevation={0}
              sx={{
                '&:before': { display: 'none' },
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '8px',
                mb: 1.5,
                '&.Mui-expanded': { margin: '0 0 12px 0' },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />}
                sx={{
                  minHeight: 'auto',
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'grey.50',
                  borderRadius: '8px 8px 0 0',
                  '&.Mui-expanded': { minHeight: 'auto' },
                  '& .MuiAccordionSummary-content': { my: '8px' },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <Typography
                    sx={{ fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.secondary' }}
                  >
                    Status
                  </Typography>
                  {safeActiveFilters.statuses && safeActiveFilters.statuses.length > 0 && (
                    <Chip size="small" label={safeActiveFilters.statuses.length} color="primary"
                      sx={{ height: 18, fontSize: '0.65rem' }} />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0.5, pb: 1 }}>
                <FormControl component="fieldset">
                  <FormGroup>
                    {statusOptions.map((status) => (
                      <FormControlLabel
                        key={status.value}
                        control={
                          <Checkbox
                            size="small"
                            checked={safeActiveFilters.statuses?.includes(status.value) || false}
                            onChange={(e) =>
                              handleMultiSelectChange('statuses', status.value, e.target.checked)
                            }
                            sx={{ py: 0.5 }}
                          />
                        }
                        label={status.label}
                        sx={{
                          ml: 0,
                          '& .MuiFormControlLabel-label': { fontSize: '0.8125rem' },
                        }}
                      />
                    ))}
                  </FormGroup>
                </FormControl>
              </AccordionDetails>
            </Accordion>
          </Box>
        </Collapse>
      </Paper>
    </LocalizationProvider>
  );
};

export default FilterSidebar;